import React, { useEffect, useRef } from 'react';

import { LoadingType, PreQualificationStep, setLoadingState, setPreQualificationStep } from 'actions';
import { setIsBorrowerManualFlow, setIsCoBorrowerManualFlow } from 'actions/application';
import { setBorrower } from 'actions/borrower';
import { setCoBorrower } from 'actions/coBorrower';
import { Button } from 'components/Buttons/Button';
import FormBody from 'components/FormBody';
import FormHeader from 'components/FormHeader';
import { PROVE_COMPLETE_AUTH_LOADER_TIMEOUT_IN_MS } from 'constants/constants';
import { BorrowerType } from 'enums/BorrowerType';
import { ButtonType } from 'enums/ButtonType';
import { LoanInformationVariables } from 'enums/LoanInformationVariables';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import * as notificationManager from 'notifications';
import { convertDateFormat } from 'utils/dateUtils';
import { getLastSymbols, removeNonDigits } from 'utils/stringUtils';
import { proveApi } from 'api/ProveApi';

import Loader from '../Loader';
import styles from './PhoneVerificationForm.module.scss';

const REQUEST_INTERVAL = 3000;
const CLEAR_INTERVAL_TIMEOUT = 5 * 60 * 1000;

export interface PhoneVerificationFormProps {
  borrowerType: BorrowerType;
}

const PhoneVerificationForm = ({ borrowerType }: PhoneVerificationFormProps) => {
  const dispatch = useAppDispatch();
  const completeAuthLoaderTimerIdRef = useRef<number | undefined>(undefined);
  const loading = useAppSelector((state) => state.loading[LoadingType.GetData]);

  const {
    application,
    borrower: { phone: borrowerPhone, dateOfBirth: borrowerDob },
    coBorrower: { phone: coBorrowerPhone, dateOfBirth: coBorrowerDob },
  } = useAppSelector((store) => store);

  const handleCompleteAuth = async () => {
    dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));

    if (borrowerType === BorrowerType.Borrower) {
      completeAuthLoaderTimerIdRef.current = window.setTimeout(() => {
        dispatch(setIsBorrowerManualFlow(false));
        dispatch(setPreQualificationStep(PreQualificationStep.LoanInformation));

        dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
      }, PROVE_COMPLETE_AUTH_LOADER_TIMEOUT_IN_MS);
    } else {
      dispatch(setIsCoBorrowerManualFlow(false));
      dispatch(setPreQualificationStep(PreQualificationStep.CoBorrowerInfo));
    }
  };

  const handleNotReceiveText = async () => {
    if (borrowerType === BorrowerType.Borrower) {
      dispatch(setIsBorrowerManualFlow(true));
      dispatch(setPreQualificationStep(PreQualificationStep.LoanInformation));
    } else {
      dispatch(setIsCoBorrowerManualFlow(true));
      dispatch(setPreQualificationStep(PreQualificationStep.CoBorrowerInfo));
    }
  };

  useEffect(() => {
    const checkIsUserCompleteAuth = async () => {
      const response = await proveApi.checkIsMFAComplete({
        sessionId: application![LoanInformationVariables.SessionId] || '',
        phoneNumber: removeNonDigits(borrowerType === BorrowerType.Borrower ? borrowerPhone : coBorrowerPhone),
        dob: convertDateFormat(borrowerType === BorrowerType.Borrower ? borrowerDob : coBorrowerDob),
      });

      return response;
    };

    const interval = setInterval(async () => {
      const result = await checkIsUserCompleteAuth();

      if (!!result?.success) {
        clearInterval(interval);

        if (borrowerType === BorrowerType.Borrower) {
          await dispatch(setBorrower({ ...result?.data, eligible: true }));
        } else {
          await dispatch(
            setCoBorrower({
              firstName: result?.data?.firstName,
              lastName: result?.data?.lastName,
              personalIdNumber: result?.data?.personalIdNumber,
              eligible: true,
            }),
          );
        }

        handleCompleteAuth();
      }

      if (result?.success === false) {
        clearInterval(interval);
        await dispatch(setBorrower({ ...result?.data, eligible: false }));

        handleNotReceiveText();
      }
    }, REQUEST_INTERVAL);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      notificationManager.notifyProveLinkExpired(handleNotReceiveText);
    }, CLEAR_INTERVAL_TIMEOUT);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
      window.clearTimeout(completeAuthLoaderTimerIdRef.current);
      notificationManager.closeAllNotification();
    };
  }, []);

  const handleBackButton = async () => {
    if (borrowerType === BorrowerType.Borrower) {
      dispatch(setPreQualificationStep(PreQualificationStep.LoanInformation));
    } else {
      dispatch(setPreQualificationStep(PreQualificationStep.BorrowerIncome));
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <FormHeader
        title={borrowerType === BorrowerType.Borrower ? `Check Your Cell Phone` : `Check Co-Borrower's Cell Phone`}
        percent={borrowerType === BorrowerType.Borrower ? 11 : 55}
      />
      <FormBody formBodyClassName={styles.formBodyMobile}>
        <div className={styles.infoContainer}>
          <span className={styles.infoTitle}>
            {borrowerType === BorrowerType.Borrower
              ? `Click the authentication link that was just sent to your cell phone (XXX) XXX-`
              : `Click the authentication link that was just sent to Co-Borrower's cell phone (XXX) XXX-`}
          </span>
          <b className={styles.boldText}>
            {getLastSymbols(borrowerType === BorrowerType.Borrower ? borrowerPhone! : coBorrowerPhone!, 4)}
          </b>
          .
        </div>
      </FormBody>
      <Button
        className={styles.didntReceiveButton}
        buttonType={ButtonType.TextButton}
        onClick={handleNotReceiveText}
        withArrow
      >
        {borrowerType === BorrowerType.Borrower ? `I didn't receive the text.` : `Co-Borrower didn't receive the text.`}
      </Button>
      <div className={styles.nextButton}>
        <Button buttonType={ButtonType.Secondary} onClick={handleBackButton} withArrow>
          Back
        </Button>
      </div>
    </>
  );
};

export default PhoneVerificationForm;
